import ChevronRight from "@mui/icons-material/ChevronRight";
import { Alert, CircularProgress, TableBody, TableCell, TableRow } from "@mui/material";
import { KeyboardEvent, useEffect, useState } from "react";
import { useActiveMessages } from "features/Messages/useActiveMessages";
import { formatZoned } from "features/Messages/time";
import type { Message } from "features/Messages/types";
import { Page, PageHeader } from "components/Common/Page";
import { CustomTableHeader, StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import * as Styles from "./PromotionsPage.styles.ts";

const PromotionsPage = () => {
	const { currentData, isLoading, error, enabled } = useActiveMessages();
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, []);
	const activePromotions = enabled
		? (currentData?.promotions ?? []).filter(
				(message) =>
					message.type === "Banner" &&
					message.showOnPromotionsPage &&
					Date.parse(message.startUtc) <= now &&
					now < Date.parse(message.endUtc)
			)
		: [];
	const handlePromotionSelect = (promotion: Message) => {
		if (promotion.linkUrl && /^https?:\/\//i.test(promotion.linkUrl)) {
			window.open(promotion.linkUrl, "_blank", "noopener,noreferrer");
		}
	};

	const handlePromotionKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, promotion: Message) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			handlePromotionSelect(promotion);
		}
	};

	return (
		<Page data-testid="promotions-page">
			<PageHeader title="Promotions" />
			<Styles.Content>
				<Styles.SectionTitle>Active Promotions</Styles.SectionTitle>
				{enabled && isLoading && <CircularProgress aria-label="Loading promotions" />}
				{enabled && error && <Alert severity="error">Unable to load promotions. Please try again.</Alert>}
				<Styles.TableContainer>
					<StyledTableWrapper
						data-testid="active-promotions-table"
						aria-label="Active Promotions Table"
					>
						<CustomTableHeader>
							<TableRow>
								<TableCell>Promo</TableCell>
								<TableCell>Start Date</TableCell>
								<TableCell>End Date</TableCell>
								<TableCell />
							</TableRow>
						</CustomTableHeader>
						<TableBody>
							{activePromotions.map((promotion) => (
								<Styles.ClickablePromotionRow
									key={promotion.messageId}
									aria-label={promotion.linkUrl ? `Open ${promotion.text} promotion` : undefined}
									data-testid={`promotion-row-${promotion.messageId}`}
									onClick={() => handlePromotionSelect(promotion)}
									onKeyDown={(event) => handlePromotionKeyDown(event, promotion)}
									role={promotion.linkUrl ? "button" : undefined}
									tabIndex={promotion.linkUrl ? 0 : undefined}
									sx={{ cursor: promotion.linkUrl ? "pointer" : "default" }}
								>
									<StyledTableCell data-testid="promotion-row-promo">
										{promotion.text}
									</StyledTableCell>
									<StyledTableCell data-testid="promotion-row-start-date">
										{formatZoned(promotion.startUtc).slice(0, 10)}
									</StyledTableCell>
									<StyledTableCell data-testid="promotion-row-end-date">
										{formatZoned(promotion.endUtc).slice(0, 10)}
									</StyledTableCell>
									<StyledTableCell
										fixedWidth="40px"
										isLastIndex
									>
										{promotion.linkUrl && <ChevronRight />}
									</StyledTableCell>
								</Styles.ClickablePromotionRow>
							))}
							{!isLoading && !error && activePromotions.length === 0 && (
								<TableRow>
									<TableCell colSpan={4}>There are no active promotions at this time.</TableCell>
								</TableRow>
							)}
						</TableBody>
					</StyledTableWrapper>
				</Styles.TableContainer>
			</Styles.Content>
		</Page>
	);
};

export default PromotionsPage;
